import React, {
 createContext, useCallback, useContext, useEffect, useMemo, useRef, useState
} from 'react'
import {
 authService, getApiErrorStatus, User
} from '../services/api'
import {
 isPublicRoute
} from '../utils/routes'
import {
 QueryClientContext
} from '@tanstack/react-query'
import {
 resetUser
} from '../analytics/posthog'
import {
 leaveForSignIn
} from '../analytics/signInReturn'
import {
  clearSensitiveBrowserState, getStoredSessionIdentity, isGlobalLogoutPending, isLogoutPending, prepareBrowserForUser, setLogoutPending
} from '../utils/browserSession'

interface AuthContextType {
  user: User | null
  isAuthenticated: boolean
  login: (loginHint?: string) => Promise<void>
  loginWithMicrosoft: (loginHint?: string) => Promise<void>
  loginWithSSO: (email: string) => Promise<void>
  logout: () => Promise<void>
  logoutEverywhere: () => Promise<void>
  setAuthUser: (user: User) => void
  isLoading: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

async function clearEndedGlobalSession(error: unknown, everywhere: boolean): Promise<boolean> {
  if (!everywhere || getApiErrorStatus(error) !== 401) return false
  try {
    // A lost response may follow a successful global commit. The old cookie
    // can no longer authorize another global request, but can still be cleared.
    await authService.logout()
    setLogoutPending(false)
    return true
  } catch {
    return false
  }
}

interface AuthProviderProps {
  children: React.ReactNode
}

export function AuthProvider({ children }: AuthProviderProps): JSX.Element {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [logoutFailed, setLogoutFailed] = useState(false)
  const [logoutNeedsSignIn, setLogoutNeedsSignIn] = useState(false)
  const queryClient = useContext(QueryClientContext)
  const globalLogout = useRef(isGlobalLogoutPending())
  const authVersion = useRef(0)
  const activeUserId = useRef<number | null>(null)

  useEffect(() => {
    const initAuth = async (): Promise<void> => {
      const version = authVersion.current
      if (isLogoutPending()) {
        try {
          if (globalLogout.current) await authService.logoutEverywhere()
          else await authService.logout()
          setLogoutPending(false)
        } catch (error) {
          if (await clearEndedGlobalSession(error, globalLogout.current)) {
            setLogoutNeedsSignIn(true)
          } else {
            setLogoutPending(true, globalLogout.current)
            setLogoutFailed(true)
          }
        }
        setIsLoading(false)
        return
      }
      // Check if we're on a public page
      const currentPath = window.location.pathname

      // If on a public page and no stored user data, skip auth check
      const hadStoredIdentity = getStoredSessionIdentity() !== null
      if (isPublicRoute(currentPath) && !hadStoredIdentity) {
        setUser(null)
        setIsLoading(false)
        return
      }

      try {
        // Try to fetch user data - cookie will be sent automatically
        const freshUser = await authService.getCurrentUser()
        if (version !== authVersion.current || isLogoutPending()) return
        prepareBrowserForUser(freshUser.id)
        activeUserId.current = freshUser.id
        setUser(freshUser)
        // Update localStorage with fresh data
        localStorage.setItem('user', JSON.stringify(freshUser))
      } catch {
        if (version !== authVersion.current || isLogoutPending()) return
        // The interceptor may already have cleared the preceding identity and
        // remembered a fresh deep link. Use the pre-request identity, and keep
        // only that destination on a second cleanup. Anonymous sign-in intents
        // belong to the visitor and have no ended account to clear.
        if (hadStoredIdentity) clearSensitiveBrowserState({ preservePendingRoute: true })
        void queryClient?.cancelQueries()
        queryClient?.clear()
        activeUserId.current = null
        setUser(null)
      }
      setIsLoading(false)
    }

    void initAuth()
    const onStorage = (event: StorageEvent): void => {
      if (event.key === 'catch.session.logoutPending' && event.newValue !== null) {
        void queryClient?.cancelQueries()
        queryClient?.clear()
        activeUserId.current = null
        authVersion.current += 1
        setUser(null)
      }
      if (event.key === 'catch.session.userId' && event.newValue !== null && activeUserId.current !== null && event.newValue !== String(activeUserId.current)) {
        void queryClient?.cancelQueries()
        queryClient?.clear()
        activeUserId.current = null
        authVersion.current += 1
        setUser(null)
      }
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [queryClient])

  // Every function on the context is identity-stable, and the context value
  // itself is memoized on the state it actually derives from. Without this,
  // each AuthProvider render handed consumers brand-new function references, so
  // any effect listing one in its dependency array re-ran on every auth-state
  // change — and an effect that itself calls `setAuthUser` (CallbackPage) fed
  // its own dependency, looping until navigation unmounted it. That loop
  // re-fired the sign-in analytics 2-3x per real sign-in (GET-10278).
  const login = useCallback(async (loginHint?: string): Promise<void> => {
    const params = sessionStorage.getItem('login_params')
    const { auth_url } = await authService.getLoginUrl(params, loginHint)
    leaveForSignIn('google', auth_url)
  }, [])

  const loginWithMicrosoft = useCallback(async (loginHint?: string): Promise<void> => {
    const params = sessionStorage.getItem('login_params')
    const { auth_url } = await authService.getMicrosoftLoginUrl(params, loginHint)
    leaveForSignIn('microsoft', auth_url)
  }, [])

  const loginWithSSO = useCallback(async (email: string): Promise<void> => {
    // Same `login_params` passthrough as the two providers above — an SSO
    // signup is a signup, and loses its utm/gclid attribution without it.
    const params = sessionStorage.getItem('login_params')
    const { auth_url } = await authService.getSSOLoginUrl(email, params)
    leaveForSignIn('sso', auth_url)
  }, [])

  const endSession = useCallback(async (everywhere: boolean): Promise<void> => {
    authVersion.current += 1
    activeUserId.current = null
    globalLogout.current = everywhere
    await queryClient?.cancelQueries()
    queryClient?.clear()
    try { resetUser() } catch { /* Analytics must not prevent sign-out. */ }
    clearSensitiveBrowserState()
    setLogoutPending(true, everywhere)
    setUser(null)
    setIsLoading(false)
    setLogoutFailed(false)
    setLogoutNeedsSignIn(false)
    try {
      // Call backend to clear the httponly cookie
      if (everywhere) await authService.logoutEverywhere()
      else await authService.logout()
      setLogoutPending(false)
      window.location.href = '/login'
    } catch (error) {
      // A JavaScript client cannot remove HttpOnly cookies when the server is
      // unreachable. Keep a pending marker across reloads and offer a retry.
      if (await clearEndedGlobalSession(error, everywhere)) {
        setLogoutNeedsSignIn(true)
      } else {
        setLogoutPending(true, everywhere)
        setLogoutFailed(true)
      }
    }
  }, [queryClient])

  const logout = useCallback(() => endSession(false), [endSession])
  const logoutEverywhere = useCallback(() => endSession(true), [endSession])

  const setAuthUser = useCallback((user: User): void => {
    if (isLogoutPending()) return
    authVersion.current += 1
    prepareBrowserForUser(user.id)
    queryClient?.clear()
    try { localStorage.setItem('user', JSON.stringify(user)) } catch { /* Storage is optional. */ }
    activeUserId.current = user.id
    setUser(user)
    setIsLoading(false)
  }, [queryClient])

  const value: AuthContextType = useMemo(
    () => ({
      user,
      isAuthenticated: !!user,
      login,
      loginWithMicrosoft,
      loginWithSSO,
      logout,
      logoutEverywhere,
      setAuthUser,
      isLoading,
    }),
    [user, login, loginWithMicrosoft, loginWithSSO, logout, logoutEverywhere, setAuthUser, isLoading],
  )

  return (
    <AuthContext.Provider value={value}>
      {children}
      {logoutNeedsSignIn && (
        <div role="alert" className="fixed bottom-4 inset-x-4 z-50 rounded-xl border border-gray-300 bg-white p-4 text-sm text-gray-800 shadow-lg">
          We couldn&apos;t confirm sign-out on your other devices. Sign in again, then choose &quot;Sign out on all devices&quot;.
        </div>
      )}
      {logoutFailed && (
        <div role="alert" className="fixed bottom-4 inset-x-4 z-50 rounded-xl border border-red-300 bg-white p-4 text-sm text-red-800 shadow-lg">
          We couldn&apos;t confirm you&apos;re signed out. Try again before leaving this device.
          <button type="button" className="ml-3 font-semibold underline" onClick={() => { void endSession(globalLogout.current) }}>Retry sign-out</button>
        </div>
      )}
    </AuthContext.Provider>
  )
}

interface StaticAuthProviderProps {
  user: User
  // Every way out of the session (logout, logout everywhere) lands here.
  onLogout: () => void
  children: React.ReactNode
}

/**
 * One fixed user with no session behind it: the onboarding demo's stand-in
 * for AuthProvider. It never reads or writes the browser's session identity,
 * so a demo run cannot sign out, or sign in over, whoever is signed in on the
 * same browser.
 */
export function StaticAuthProvider({ user: initialUser, onLogout, children }: StaticAuthProviderProps): JSX.Element {
  const [user, setUser] = useState(initialUser)
  const signIn = useCallback(async (): Promise<void> => {}, [])
  const signOut = useCallback(async (): Promise<void> => onLogout(), [onLogout])

  const value: AuthContextType = useMemo(
    () => ({
      user,
      isAuthenticated: true,
      login: signIn,
      loginWithMicrosoft: signIn,
      loginWithSSO: signIn,
      logout: signOut,
      logoutEverywhere: signOut,
      setAuthUser: setUser,
      isLoading: false,
    }),
    [user, signIn, signOut],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
